import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, SendHorizonal, Square } from 'lucide-react';
import { ModelPicker } from '@/components/ai/ModelPicker';
import { useVoiceRecorder, extForMime } from '@/hooks/useVoiceRecorder';
import { commsApi } from '@/utils/commsApi';

export const CHAT_DICTATION_MAX_SECONDS = 120;

// Message box: Enter sends, Shift+Enter adds a line; mic dictates into the box; Stop cancels the streaming reply.
export const ChatComposer = ({ value, onChange, onSend, onStop, sending, models, model, onModelChange }) => {
  const ref = useRef(null);
  const [transcribing, setTranscribing] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(''), 6000);
    return () => clearTimeout(t);
  }, [note]);

  const dictation = useVoiceRecorder({
    maxSeconds: CHAT_DICTATION_MAX_SECONDS,
    onClip: async ({ blob, mimeType }) => {
      const file = new File([blob], `chat-dictation.${extForMime(mimeType)}`, { type: mimeType.split(';')[0] });
      setTranscribing(true);
      try {
        const { text: spoken } = await commsApi.transcribe(file, navigator.language?.slice(0, 2));
        if (!spoken) { setNote('No speech detected — try again a little closer to the microphone.'); return; }
        onChange(value ? `${value.replace(/\s+$/, '')} ${spoken}` : spoken);
        requestAnimationFrame(() => ref.current?.focus());
      } catch (err) {
        setNote(err?.response?.data?.detail || 'Transcription failed. Please try again.');
      } finally {
        setTranscribing(false);
      }
    },
    onError: (err) => {
      if (err?.message === 'too_short') setNote('Keep recording a little longer.');
      else if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') setNote('Microphone access was denied. Allow it for this site in your browser settings, then try again.');
      else if (err?.message === 'unsupported') setNote('Voice recording is not supported in this browser.');
      else setNote('Could not start recording.');
    },
  });

  const toggleDictation = () => (dictation.isRecording ? dictation.stop() : dictation.start());
  const micBusy = transcribing || sending;
  const micLabel = dictation.isRecording
    ? `Listening… ${Math.floor(dictation.elapsed)}s — tap to finish`
    : transcribing ? 'Transcribing…' : 'Dictate your message';

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!sending && value.trim()) onSend();
    }
  };

  return (
    <div className="chat-composer" data-testid="chat-composer">
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={dictation.isRecording ? 'Listening…' : 'Message Vibely AI… (Enter to send, Shift+Enter for a new line)'}
        aria-label="Message"
        data-testid="chat-input"
      />
      <div className="chat-composer-bar">
        <div className="chat-composer-left">
          <button
            type="button"
            className={`chat-mic ${dictation.isRecording ? 'recording' : ''}`}
            onClick={toggleDictation}
            disabled={micBusy}
            aria-pressed={dictation.isRecording}
            aria-label={dictation.isRecording ? 'Stop dictation' : 'Dictate your message'}
            title={micLabel}
            data-testid="chat-mic-button"
          >
            {transcribing ? <Loader2 size={15} className="animate-spin" /> : dictation.isRecording ? <Square size={13} /> : <Mic size={15} />}
            {dictation.isRecording && <span className="chat-mic-time" data-testid="chat-mic-time">{Math.floor(dictation.elapsed)}s</span>}
          </button>
          <ModelPicker models={models} value={model} onChange={onModelChange} compact disabled={sending} testId="chat-model-picker" />
        </div>
        {sending ? (
          <button type="button" className="chat-send stop" onClick={onStop} aria-label="Stop generating" data-testid="chat-stop-button">
            <Square size={15} /> Stop
          </button>
        ) : (
          <button type="button" className="chat-send" onClick={onSend} disabled={!value.trim()} aria-label="Send message" data-testid="chat-send-button">
            <SendHorizonal size={16} />
          </button>
        )}
      </div>
      {(note || dictation.isRecording || transcribing) && (
        <div className={`chat-composer-note ${note ? 'error' : ''} ${dictation.isRecording ? 'recording' : ''}`} role={note ? 'alert' : 'status'} data-testid="chat-composer-note">
          {dictation.isRecording && <span className="rec-dot" />}
          {note || micLabel}
        </div>
      )}
    </div>
  );
};
